import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { supabase } from './supabaseClient';
import Login from './pages/Login.js';
import Dashboard from './pages/Dashboard.js';
import Suppliers from './pages/Suppliers.js';
import AddSupplier from './pages/AddSupplier.js';
import SupplierDetail from './pages/SupplierDetail.js';
import Briefs from './pages/Briefs.js';
import AddBrief from './pages/AddBrief.js';
import BriefDetail from './pages/BriefDetail.js';
import ComparisonView from './pages/ComparisonView.js';
import ResponseDetail from './pages/ResponseDetail.js';
import SupplierPortal from './pages/SupplierPortal.js';
import SupplierCompliance from './pages/SupplierCompliance.js';
import SupplierResponse from './pages/SupplierResponse.js';
import AdminCompliance from './pages/AdminCompliance.js';
import AdminComplianceReview from './pages/AdminComplianceReview.js';
import ComplianceDocumentDetail from './pages/ComplianceDocumentDetail.js';
import ComplianceDocuments from './pages/ComplianceDocuments.js';
import Handbook from './pages/Handbook.js';
import DocumentsInfo from './pages/DocumentsInfo.js';
import SuppliersInfo from './pages/SuppliersInfo.js';
import BriefsInfo from './pages/BriefsInfo.js';
import ComplianceInfo from './pages/ComplianceInfo.js';
import HostedFormEditor from './pages/HostedFormEditor.js';
import PdfFieldEditor from './pages/PdfFieldEditor.js';
import DocumentVersions from './pages/DocumentVersions.js';
import SupplierDocumentHistory from './pages/SupplierDocumentHistory.js';
import Debriefs from './pages/Debriefs.js';
import DebriefDetail from './pages/DebriefDetail.js';
import Admin from './pages/Admin.js';
import AdminTags from './pages/AdminTags.js';
import Reporting from './pages/Reporting.js';
import Pinned from './pages/Pinned.js';
import ChangePassword from './pages/ChangePassword.js';
import ForgotPassword from './pages/ForgotPassword.js';
import SupplierSetupForm from './pages/SupplierSetupForm.js';
import DelegatedDocumentPage from './pages/DelegatedDocumentPage.js';
import HelpUploadCompliance from './pages/HelpUploadCompliance.js';
import HelpRequiredDocuments from './pages/HelpRequiredDocuments.js';
import HelpHowBriefsWork from './pages/HelpHowBriefsWork.js';
import HelpCompliance from './pages/HelpCompliance.js';
import HelpRecognition from './pages/HelpRecognition.js';
import NotFound from './pages/NotFound.js';
// Throwaway preview page for reviewing every email template in bulk.
// Delete this import + the /dev/email-previews route below once the
// bulk review is done.
import DevEmailPreviews from './pages/DevEmailPreviews.js';
import OttoGalleryPage from './pages/OttoGalleryPage.js';
import { TurboParadeProvider } from './components/turbo';
import { OttoRoamer, OttoDevPanel, OttoMotionPreference, OttoFavicon } from './components/otto';
import DocumentTitle from './components/DocumentTitle';

function App() {
  const [session, setSession] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRecovery, setIsRecovery] = useState(false);
  // Tracks whether we've already established who this user is. Used by the
  // onAuthStateChange listener to distinguish a real new sign-in (no
  // userType yet → safe to show the loading curtain) from a background
  // re-validation that fires SIGNED_IN on focus return (userType already
  // known → must NOT flip loading=true, otherwise the whole tree unmounts
  // and every page resets its local state, including the supplier card's
  // active tab).
  const userTypeRef = useRef(null);

  useEffect(() => {
    const checkUser = async (session) => {
      if (!session) { setUserType(null); userTypeRef.current = null; setLoading(false); return; }
      const { data: internal } = await supabase.from('internal_users').select('id, is_active').eq('id', session.user.id).maybeSingle();
      if (internal) {
        if (internal.is_active === false) {
          await supabase.auth.signOut();
          setSession(null);
          setUserType(null);
          userTypeRef.current = null;
          setLoading(false);
          return;
        }
        setUserType('internal');
        userTypeRef.current = 'internal';
      } else {
        const { data: supplier } = await supabase.from('supplier_users').select('id, is_active').eq('id', session.user.id).maybeSingle();
        if (supplier?.is_active === false) {
          await supabase.auth.signOut();
          setSession(null);
          setUserType(null);
          userTypeRef.current = null;
          setLoading(false);
          return;
        }
        setUserType('supplier');
        userTypeRef.current = 'supplier';
      }
      setLoading(false);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      checkUser(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true);
        setSession(session);
        setLoading(false);
        return;
      }
      // Token refresh / user-update doesn't change userType — sync session and skip
      // the DB round-trip so we don't blank the UI mid-flow.
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        setSession(session);
        return;
      }
      setSession(session);
      // Only flip loading=true on a genuine new sign-in (we don't have a
      // userType cached yet). Background re-validations after focus
      // return fire SIGNED_IN with a session we already know about —
      // setting loading=true there would unmount the BrowserRouter via
      // the `if (loading) return null` guard below, resetting every
      // page's state. checkUser still runs to catch role/active changes,
      // it just doesn't curtain the UI while it does.
      if (session && !userTypeRef.current) setLoading(true);
      checkUser(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) return null;

  const defaultRoute = isRecovery ? <Navigate to="/change-password" /> :
    !session ? <Login /> :
    userType === 'internal' ? <Navigate to="/dashboard" /> :
    <Navigate to="/portal" />;

  return (
    <BrowserRouter>
      {/* TurboParadeProvider listens globally for the T-U-R-B-O konami
          sequence and exposes the parade's current accessory to any
          TurboIdle that's rendered. Lives at the app root so the
          state survives route changes. Supplier portal pages don't
          render TurboIdle, so the listener has no visible effect
          there even though it's mounted. */}
      <TurboParadeProvider>
      <DocumentTitle />
      {/* One page crashing must not take the application with it. Without a
          boundary here, a render error unmounts the whole root — and since the
          router sits above it, clicking the menu then changes the URL while
          nothing renders at all, which reads as the app having seized rather
          than one page having failed. Keyed on the pathname so navigating away
          from a broken page clears it without a reload. */}
      <RouteErrorBoundary>
      <Routes>
        <Route path="/supplier-form/:token" element={<SupplierSetupForm />} />
        {/* Delegated document — a colleague at a supplier completing or
            signing ONE document. Public and session-less by design: the token
            is resolved server-side and the delegate holds no database access
            of their own, so this must sit above the auth-gated routes. */}
        <Route path="/d/:token" element={<DelegatedDocumentPage />} />
        <Route path="/" element={defaultRoute} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/dashboard" element={session && userType === 'internal' ? <Dashboard /> : <Navigate to="/" />} />
        <Route path="/suppliers" element={session && userType === 'internal' ? <Suppliers /> : <Navigate to="/" />} />
        <Route path="/suppliers/new" element={session && userType === 'internal' ? <AddSupplier /> : <Navigate to="/" />} />
        <Route path="/suppliers/:id" element={session && userType === 'internal' ? <SupplierDetail /> : <Navigate to="/" />} />
        <Route path="/briefs" element={session && userType === 'internal' ? <Briefs /> : <Navigate to="/" />} />
        <Route path="/compliance" element={session && userType === 'internal' ? <AdminCompliance /> : <Navigate to="/" />} />
        <Route path="/compliance/:docId/review" element={session && userType === 'internal' ? <AdminComplianceReview /> : <Navigate to="/" />} />
        <Route path="/compliance/:docId/detail" element={session && userType === 'internal' ? <ComplianceDocumentDetail /> : <Navigate to="/" />} />
        <Route path="/documents" element={session && userType === 'internal' ? <ComplianceDocuments /> : <Navigate to="/" />} />
        {/* The one long-form explainer. Internal but NOT admin-gated: it's linked from the welcome email, which every new staff account gets. */}
        <Route path="/handbook" element={session && userType === 'internal' ? <Handbook /> : <Navigate to="/" />} />
        <Route path="/documents/how-it-works" element={session && userType === 'internal' ? <DocumentsInfo /> : <Navigate to="/" />} />
        {/* One explainer per main-menu area. Each sits UNDER its own route so
            the back link and the sidebar highlight stay where the reader was. */}
        <Route path="/suppliers/how-it-works" element={session && userType === 'internal' ? <SuppliersInfo /> : <Navigate to="/" />} />
        <Route path="/briefs/how-it-works" element={session && userType === 'internal' ? <BriefsInfo /> : <Navigate to="/" />} />
        <Route path="/compliance/how-it-works" element={session && userType === 'internal' ? <ComplianceInfo /> : <Navigate to="/" />} />
        <Route path="/documents/:docId/build" element={session && userType === 'internal' ? <HostedFormEditor /> : <Navigate to="/" />} />
        <Route path="/documents/:docId/pdf-fields" element={session && userType === 'internal' ? <PdfFieldEditor /> : <Navigate to="/" />} />
        <Route path="/documents/:docId/versions" element={session && userType === 'internal' ? <DocumentVersions /> : <Navigate to="/" />} />
        <Route path="/debriefs" element={session && userType === 'internal' ? <Debriefs /> : <Navigate to="/" />} />
        <Route path="/debriefs/:debriefId" element={session && userType === 'internal' ? <DebriefDetail /> : <Navigate to="/" />} />
        <Route path="/briefs/new" element={session && userType === 'internal' ? <AddBrief /> : <Navigate to="/" />} />
        <Route path="/briefs/:id/edit" element={session && userType === 'internal' ? <AddBrief /> : <Navigate to="/" />} />
        <Route path="/briefs/:id" element={session && userType === 'internal' ? <BriefDetail /> : <Navigate to="/" />} />
        <Route path="/briefs/:id/compare" element={session && userType === 'internal' ? <ComparisonView /> : <Navigate to="/" />} />
        <Route path="/briefs/:id/responses/:supplierId" element={session && userType === 'internal' ? <ResponseDetail /> : <Navigate to="/" />} />
        <Route path="/portal" element={session && userType === 'supplier' ? <SupplierPortal /> : <Navigate to="/" />} />
        <Route path="/portal/compliance" element={session && userType === 'supplier' ? <SupplierCompliance /> : <Navigate to="/" />} />
        <Route path="/portal/compliance/:documentKey/history" element={session && userType === 'supplier' ? <SupplierDocumentHistory /> : <Navigate to="/" />} />
        <Route path="/portal/brief/:id" element={session && userType === 'supplier' ? <SupplierResponse /> : <Navigate to="/" />} />
        {/* Supplier-facing help articles — reachable from the
            slide-out's Common Topics list. Gated to authenticated
            suppliers; admins land back on / via the Navigate fallback. */}
        <Route path="/help/upload-compliance" element={session && userType === 'supplier' ? <HelpUploadCompliance /> : <Navigate to="/" />} />
        <Route path="/help/required-documents" element={session && userType === 'supplier' ? <HelpRequiredDocuments /> : <Navigate to="/" />} />
        <Route path="/help/how-briefs-work" element={session && userType === 'supplier' ? <HelpHowBriefsWork /> : <Navigate to="/" />} />
        <Route path="/help/compliance" element={session && userType === 'supplier' ? <HelpCompliance /> : <Navigate to="/" />} />
        <Route path="/help/recognition" element={session && userType === 'supplier' ? <HelpRecognition /> : <Navigate to="/" />} />
        {/* Retired Bronze/Silver/Gold badge page — redirect to the new
            recognition (growing-oak) help page. */}
        <Route path="/help/badges" element={session && userType === 'supplier' ? <Navigate to="/help/recognition" replace /> : <Navigate to="/" />} />
        <Route path="/admin" element={session && userType === 'internal' ? <Admin /> : <Navigate to="/" />} />
        <Route path="/admin/tags" element={session && userType === 'internal' ? <AdminTags /> : <Navigate to="/" />} />
        <Route path="/reporting" element={session && userType === 'internal' ? <Reporting /> : <Navigate to="/" />} />
        <Route path="/pinned" element={session && userType === 'internal' ? <Pinned /> : <Navigate to="/" />} />
        <Route path="/change-password" element={session || isRecovery ? <ChangePassword /> : <Navigate to="/" />} />
        {/* Throwaway: bulk email-preview sender for copy review.
            Internal users only. Delete this route + DevEmailPreviews
            import + the file itself once the review is complete. */}
        <Route path="/dev/email-previews" element={session && userType === 'internal' ? <DevEmailPreviews /> : <Navigate to="/" />} />
        <Route path="/dev/otto" element={session && userType === 'internal' ? <OttoGalleryPage /> : <Navigate to="/" />} />
        {/* Catch-all 404. Internal admins get the Turbo treatment;
            suppliers and signed-out visitors fall back to the default
            route so they land on their portal or Login rather than an
            internal-product page. */}
        <Route path="*" element={session && userType === 'internal' ? <NotFound /> : <Navigate to="/" />} />
      </Routes>
      </RouteErrorBoundary>
      {/* The roaming Otto overlay — the surface the idle set travels on
          when a shot needs him outside a component's bounds. Mounted at
          the app root so a shot survives route changes, and gated to
          internal users: Otto is never a supplier surface. It renders
          nothing at all between shots, and OttoRoamer bails entirely
          under reduced motion or on touch. */}
      {/* Carries the per-user "animate Otto" preference onto the root element,
          where both otto.css and the JS schedulers read it. Mounted beside the
          roamer rather than inside it because the preference has to hold for
          every Otto surface, including the loading curtain that is on screen
          before any of this renders. */}
      {/* Otto in the tab for staff, the neutral Fusion mark for everyone else
          — including the shared login page, which cannot know who is looking at
          it until sign-in completes. Mounted UNGATED, unlike its siblings:
          reverting the icon for suppliers is the whole job. */}
      <OttoFavicon isInternal={!!session && userType === 'internal'} />
      {session && userType === 'internal' && <OttoMotionPreference />}
      {session && userType === 'internal' && <OttoRoamer />}
      {/* Hidden QA trigger for the idle set. Invisible until the code word is
          typed — see OttoDevPanel. Internal only, same as the roamer it drives. */}
      {session && userType === 'internal' && <OttoDevPanel />}
      {/* AlchemyFooter (the fixed corner "Powered by CORE" badge) removed —
          it duplicated the per-surface footers: AppFooter on the admin +
          supplier pages and the AuthShell badge on login. Each surface now
          carries its own single CORE attribution. */}
      </TurboParadeProvider>
    </BrowserRouter>
  );
}

// Lives inside BrowserRouter so it can read the current path — the boundary
// resets itself whenever that changes, so leaving a broken page is enough to
// recover.
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

export default App;